// react_app/src/api/translationService.ts

import { apiClient } from './apiClient';

// --- DTOs ---

export interface LanguageDto {
  id: string;
  cultureName: string;
  uiCultureName: string;
  displayName: string;
  flagIcon: string | null;
  isEnabled: boolean;
  isDefaultLanguage: boolean;
  concurrencyStamp: string | null;
}

export interface LanguageTextDto {
  resourceName: string;
  cultureName: string;
  baseCultureName: string;
  baseValue: string;
  name: string;
  value: string;
}

export interface GetLanguageTextsInput {
  resourceName?: string;
  baseCultureName?: string;
  targetCultureName?: string;
  filter?: string;
  getOnlyEmptyValues?: boolean;
  sorting?: string;
  skipCount?: number;
  maxResultCount?: number;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

// --- API Functions ---

const RESOURCE_NAME = 'learnleaf';
const BASE_CULTURE = 'en';

export const translationApi = {
  // --- Languages ---

  async getAllLanguages(): Promise<LanguageDto[]> {
    const response = await apiClient.get<{ items: LanguageDto[] }>(
      '/api/language-management/languages/all'
    );
    return response.data.items;
  },

  async updateLanguage(
    id: string,
    data: { displayName: string; flagIcon: string | null; isEnabled: boolean; concurrencyStamp: string | null }
  ): Promise<LanguageDto> {
    const response = await apiClient.put<LanguageDto>(
      `/api/language-management/languages/${id}`,
      data
    );
    return response.data;
  },

  async setDefaultLanguage(id: string): Promise<void> {
    await apiClient.put(`/api/language-management/languages/${id}/set-as-default`);
  },

  // --- Language Texts ---

  async getLanguageTexts(
    input: GetLanguageTextsInput
  ): Promise<PagedResult<LanguageTextDto>> {
    const response = await apiClient.get<PagedResult<LanguageTextDto>>(
      '/api/language-management/language-texts',
      {
        params: {
          resourceName: input.resourceName ?? RESOURCE_NAME,
          baseCultureName: input.baseCultureName ?? BASE_CULTURE,
          targetCultureName: input.targetCultureName,
          filter: input.filter || undefined,
          getOnlyEmptyValues: input.getOnlyEmptyValues || undefined,
          sorting: input.sorting || undefined,
          skipCount: input.skipCount ?? 0,
          maxResultCount: input.maxResultCount ?? 50,
        },
      }
    );
    return response.data;
  },

  async updateLanguageText(
    cultureName: string,
    keyName: string,
    value: string
  ): Promise<void> {
    await apiClient.put(
      `/api/language-management/language-texts/${RESOURCE_NAME}/${cultureName}/${encodeURIComponent(keyName)}`,
      null,
      { params: { value } }
    );
  },

  async restoreLanguageText(
    cultureName: string,
    keyName: string
  ): Promise<void> {
    await apiClient.put(
      `/api/language-management/language-texts/${RESOURCE_NAME}/${cultureName}/${encodeURIComponent(keyName)}/restore`
    );
  },

  async getAllLanguageTexts(
    targetCultureName: string
  ): Promise<LanguageTextDto[]> {
    const allItems: LanguageTextDto[] = [];
    let skipCount = 0;
    const maxResultCount = 100;

    while (true) {
      const page = await this.getLanguageTexts({
        targetCultureName,
        skipCount,
        maxResultCount,
      });
      allItems.push(...page.items);
      if (allItems.length >= page.totalCount) break;
      skipCount += maxResultCount;
    }

    return allItems;
  },
};
